import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { DealerDogBrand } from "@/components/layout/dealerdog-brand";
import { PublicSiteHeader } from "@/components/layout/public-site-header";
import {
  CALENDLY_URL,
  DEMO_LOOM_EMBED_URL,
  DEMO_LOOM_URL,
} from "@/lib/site-links";
import "@/styles/dd-public.css";

const TRUST_ITEMS = [
  "Dealer-first workflow",
  "Soft-credit prequalification",
  "Human financing support",
];

/** On-site Loom walkthrough at /demo (replaces hard redirect to Loom). */
export function DemoPage() {
  const year = new Date().getFullYear();
  const canonicalUrl = `${window.location.origin}/demo`;

  return (
    <div className="dd-public-page dd-demo-page">
      <Helmet>
        <title>Watch DealerDog in Action · DealerDog</title>
        <meta
          name="description"
          content="See how DealerDog helps equipment dealers move shoppers toward financing with a streamlined dealer-first workflow."
        />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content="Watch DealerDog in Action · DealerDog" />
        <meta
          property="og:description"
          content="See how DealerDog helps equipment dealers move shoppers toward financing with a streamlined dealer-first workflow."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonicalUrl} />
      </Helmet>

      <PublicSiteHeader variant="home" />

      <main className="dd-demo-main" id="main-content">
        <div className="dd-container">
          <section className="dd-demo-hero">
            <h1>
              See DealerDog <strong>in action.</strong>
            </h1>
            <p>
              See how DealerDog helps equipment dealers keep financing conversations moving, from
              the first customer question to the next step.
            </p>
          </section>

          <section className="dd-demo-video-shell" aria-label="DealerDog demo video">
            <div className="dd-demo-video">
              <iframe
                src={DEMO_LOOM_EMBED_URL}
                title="DealerDog platform walkthrough"
                loading="lazy"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                referrerPolicy="strict-origin-when-cross-origin"
                allowFullScreen
              />
            </div>
            <p className="dd-demo-video-caption">
              Watch the DealerDog platform walkthrough without leaving our website.{" "}
              <a href={DEMO_LOOM_URL} target="_blank" rel="noopener noreferrer">
                Open on Loom
              </a>
            </p>
          </section>

          <section className="dd-demo-cta">
            <h2>
              Ready to turn more shoppers into{" "}
              <span className="dd-demo-accent">funded buyers?</span>
            </h2>
            <p>
              Give your team a faster way to guide commercial financing conversations. Get started
              with DealerDog today.
            </p>
            <div className="button-row dd-demo-cta-actions">
              <Link className="button button-primary" to="/#create-account">
                Create an Account
              </Link>
              <a className="button button-secondary" href={CALENDLY_URL}>
                Schedule a Demo
              </a>
            </div>
            <div className="dd-demo-trust">
              {TRUST_ITEMS.map((item) => (
                <span key={item}>{item}</span>
              ))}
            </div>
          </section>
        </div>
      </main>

      <footer className="dd-demo-footer">
        <div className="dd-container">
          <div className="dd-demo-footer-top">
            <div>
              <Link className="brand" to="/" aria-label="DealerDog home">
                <DealerDogBrand />
              </Link>
              <p className="dd-demo-footer-desc">
                DealerDog gives equipment dealers fast financing direction at the point of sale, so
                your team can keep customers moving forward.
              </p>
            </div>
            <div className="dd-demo-footer-links">
              <div>
                <h3>Explore</h3>
                <a href="/#platform">Platform</a>
                <a href="/#how-it-works">How It Works</a>
                <Link to="/demo">Watch Demo</Link>
                <a href={CALENDLY_URL}>Schedule a Demo</a>
              </div>
              <div>
                <h3>Legal</h3>
                <Link to="/privacy">Privacy Policy</Link>
                <Link to="/">Home</Link>
              </div>
            </div>
          </div>
          <div className="dd-demo-footer-bottom">
            <span>© {year} TrueCore Capital, LLC, doing business as DealerDog.</span>
            <span>Built for equipment dealers.</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
